import { Injectable } from '@angular/core';
import { SUBMIT_DRY_RUN } from './submit-dry-run';

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {

  public wwwUrl: string;
  public adminUrl: string;
  public rateTableUrl: string;
  public apiKey: string = 'bcef7b5f-31a4-48b3-8bcc-9012f6a4330f';
  public fruApiUrl: string; // Laravel API
  public fruAPI: string; // CodeIgniter API
  public effectiveAdvertisedRatesEnabled: boolean = false;

  initAppConfig() {
    let hostname: string = window.location.hostname;
    let forceProdComposeApi = false;   // abx 456: ?fruApi=prod scopes to fruApiUrl ONLY (applied after derivation)
    this.effectiveAdvertisedRatesEnabled = (window as any).publicEffectiveAdvertisedRatesEnabled === true;

    // FRU-23491: the fixture build only (null in every real build). The page guard goes in before any request, and every
    // call stays on the page's own origin, where the bench answers it. On any other host the guard refuses everything.
    if (SUBMIT_DRY_RUN) {
      SUBMIT_DRY_RUN.install();
      this.wwwUrl = this.adminUrl = this.rateTableUrl = this.fruAPI = '';
      this.fruApiUrl = SUBMIT_DRY_RUN.apiOrigin || '';   // the guard re-addresses it onto the bench
      return;
    }

    // Local dev (ng serve on localhost / 127.0.0.1 / ::1 / a bare IP such as a
    // Tailscale address): use relative URLs so the dev-server proxy
    // (proxy.conf.json) forwards API calls to the real FRU backends.
    // Prod hosts are domain names, so this branch never fires in prod.
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
      this.wwwUrl = '';
      this.adminUrl = '';
      this.rateTableUrl = '';
      this.fruApiUrl = '';
      this.fruAPI = '';
      return;
    }

    this.wwwUrl = `https://${hostname}`;

    // FRU-21526 HOPE 2.0 (Ed 7/19, TEST-ONLY): on NON-prod hosts, ?fruApi=prod points the API at the
    // production fru-api so edoffice testing exercises the real fast lane (haiku compose). The www
    // hostname guard makes this structurally impossible to trigger on production.
    // FRU-21526 HOPE 2.0 (Ed 7/19, TEST-ONLY): on the edoffice dev host, ?fruApi=prod exercises the real
    // haiku compose fast lane. abx 456 (QA safety): scope this to ONLY the Laravel compose/returning base
    // (fruApiUrl). It must NOT repoint adminUrl/rateTableUrl/fruAPI -- submitLead() and consent calls POST
    // to adminUrl, so a broad override would let an edoffice QA flow create a REAL production lead. Flag it
    // here and apply the narrow fruApiUrl-only override AFTER the normal (edoffice) derivation below, so
    // adminUrl/rateTableUrl/fruAPI keep their edoffice-* dev values.
    try {
      forceProdComposeApi = (hostname === 'edoffice-www.freerateupdate.com' && new URLSearchParams(window.location.search).get('fruApi') === 'prod');   // abx: EXACT dev host only
    } catch (e) { /* non-fatal */ }

    const testDomains = [
      'www.freerateupdate.com',
      'loandepot.freerateupdate.com',
      'ccm.freerateupdate.com',
      'rocket.freerateupdate.com',
      'figure.freerateupdate.com',
      'leads.freerateupdate.com'
    ];
    const matchingTestDomain = testDomains.find(domain => hostname.includes(domain));

    if (matchingTestDomain) {
      this.adminUrl = this.wwwUrl.replace(matchingTestDomain, 'admin.freerateupdate.com');
      this.rateTableUrl = this.wwwUrl.replace(matchingTestDomain, 'ratetables.freerateupdate.com');
      this.fruAPI = this.wwwUrl.replace(matchingTestDomain, 'api.freerateupdate.com');
      this.fruApiUrl = this.wwwUrl.replace(matchingTestDomain, 'fru-api.freerateupdate.com');
    } else {
      // See if we're in prod
      if (/^www/.test(hostname)) {
        this.adminUrl = 'https://admin.freerateupdate.com';
        this.rateTableUrl = 'https://ratetables.freerateupdate.com';
        this.fruApiUrl = 'https://fru-api.freerateupdate.com';
        this.fruAPI = 'https://api.freerateupdate.com';
      } else {
        // Get subdomain and make dev urls
        let allSplitUp = hostname.split('.');
        let subdomain = allSplitUp[0];
        let serviceSubdomain = subdomain.endsWith('-admin') ? subdomain.substring(0, subdomain.length - 6) : subdomain;

        if (subdomain === 'admin') {
          this.adminUrl = 'https://admin.freerateupdate.com';
          this.rateTableUrl = 'https://ratetables.freerateupdate.com';
          this.fruApiUrl = 'https://fru-api.freerateupdate.com';
          this.fruAPI = 'https://api.freerateupdate.com';
        } else {
          this.adminUrl = `https://${serviceSubdomain}-admin.freerateupdate.com`;
          this.rateTableUrl = `https://${serviceSubdomain}-ratetables.freerateupdate.com`;
          this.fruApiUrl = `https://${serviceSubdomain}-fru-api.freerateupdate.com`;
          this.fruAPI = `https://${serviceSubdomain}-api.freerateupdate.com`;
        }
      }
    }
    // abx 456: narrow prod override -- Laravel compose/returning base ONLY; lead-submit + consent (adminUrl),
    // rate tables, and the CodeIgniter api stay on their edoffice-* dev endpoints.
    if (forceProdComposeApi) { this.fruApiUrl = 'https://fru-api.freerateupdate.com'; }
  }

  constructor() {
    this.initAppConfig();
  }
}
